import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Save, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * WhatsApp-style Group Description editor. Admin-only — reachable by
 * tapping the description text (or "+ Add description" ghost link)
 * under the group name in the chat header.
 */
export default function GroupDescriptionEditor({ group, onClose, onSaved }) {
  const [desc, setDesc] = useState(group.description || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/groups/${group.id}/description`, {
        description: desc.trim(),
      });
      toast.success("Description saved");
      onSaved && onSaved(desc.trim());
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="group-description-editor-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="group-description-editor"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#DCDCD3] bg-[#F5F5F0]">
          <div
            className="text-sm font-semibold text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Group Description · {group.name}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="group-description-close"
            className="p-1.5 rounded-full text-[#6B6B61] hover:bg-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-2">
          <div className="flex items-center gap-2 text-[10px] text-[#6B6B61] uppercase tracking-widest">
            <Info className="w-3 h-3" />
            Visible to every member of the group
          </div>
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value.slice(0, 500))}
            rows={5}
            placeholder="Add a description for this group — will be visible to every member under the group name."
            data-testid="group-description-textarea"
            className="w-full px-3 py-2 text-sm bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg focus:outline-none focus:border-[#385C4B] resize-none"
            autoFocus
          />
          <div className="text-[10px] text-[#6B6B61] text-right font-mono">
            {desc.length} / 500
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[#DCDCD3] bg-[#F5F5F0] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-[#6B6B61] px-3 py-2 rounded-lg hover:bg-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="group-description-save"
            className="bg-[#385C4B] text-white text-xs px-4 py-2 rounded-lg hover:bg-[#2D4A3C] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
